// src/components/PaymentForm.tsx
import React, { useState, useEffect } from 'react';
import { Button, Input } from './ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

interface PaymentFormProps {
  studentId: number;
  payment: Payment | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface PaymentFormData {
  year: number;
  payment_date: string;
  amount: number;
  book_number: string;
  receipt_number: string;
}

interface Year {
  id: number;
  short_name: string;
  full_name: string;
}

interface Payment {
  id: number;
  person_id: number;
  year: number;
  payment_date: string;
  amount: number;
  book_number: string;
  receipt_number: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ studentId, payment, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<PaymentFormData>({
    year: new Date().getFullYear(),
    payment_date: new Date().toISOString().split('T')[0],
    amount: 0,
    book_number: '',
    receipt_number: '',
  });

  const [years, setYears] = useState<Year[]>([]);
  const isEditing = !!payment;

  const fetchYears = async () => {
    try {
      const response = await fetch('/api/years');
      if (response.ok) {
        const data = await response.json();
        setYears(data);
      }
    } catch (error) {
      console.error('Error fetching years:', error);
    }
  };

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    if (payment) {
      setFormData({
        year: payment.year,
        payment_date: payment.payment_date,
        amount: payment.amount,
        book_number: payment.book_number,
        receipt_number: payment.receipt_number,
      });
    } else {
      setFormData({
        year: new Date().getFullYear(),
        payment_date: new Date().toISOString().split('T')[0],
        amount: 0,
        book_number: '',
        receipt_number: '',
      });
    }
  }, [payment]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let url = `/api/students/${studentId}/payments`;
      let method = 'POST';
      
      if (isEditing && payment) {
        url = `${url}/${payment.id}`;
        method = 'PUT';
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        onSuccess();
        onClose();
      } else {
        const error = await response.json();
        throw new Error(error.message || '保存失败');
      }
    } catch (error) {
      console.error('Error saving payment:', error);
      alert('保存失败，请重试');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? '编辑缴费记录' : '新增缴费记录'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">年度</label>
            <select
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">请选择年度</option>
              {years.map(year => (
                <option key={year.id} value={year.id}>
                  {year.full_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">缴费日期</label>
            <Input
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleInputChange}
              required
              className="mt-1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">金额</label>
            <Input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              required
              step="0.01"
              min="0"
              className="mt-1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">分册编号</label>
            <Input
              type="text"
              name="book_number"
              value={formData.book_number}
              onChange={handleInputChange}
              required
              className="mt-1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">凭证编号</label>
            <Input
              type="text"
              name="receipt_number"
              value={formData.receipt_number}
              onChange={handleInputChange}
              required
              className="mt-1"
            />
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              取消
            </Button>
            <Button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {isEditing ? '更新' : '保存'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentForm;